import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

function formatError(detail) {
  if (!detail) return 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

const ROLE_OPTIONS = [
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'site_manager', label: 'Site Manager' },
  { value: 'project_manager', label: 'Project Manager' },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '', role: 'subcontractor', packages: [] });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get('/packages').then(r => setPackages(r.data)).catch(() => {});
  }, []);

  const togglePackage = (pkg) => {
    setForm(prev => ({
      ...prev,
      packages: prev.packages.includes(pkg) ? prev.packages.filter(p => p !== pkg) : [...prev.packages, pkg]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success('Account created!');
      navigate('/');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logo.jpg" alt="SiteFlow" className="h-10 w-auto rounded mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Create Account</h1>
          <p className="text-sm text-slate-500 mt-1">SiteFlow — 60 East Street</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="register-form">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input id="name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Smith" required data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input id="company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Company name" data-testid="register-company-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-email">Email</Label>
            <Input id="reg-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@example.com" required data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-password">Password</Label>
            <div className="relative">
              <Input id="reg-password" type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Min 6 characters" required minLength={6} data-testid="register-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={v => setForm({...form, role: v})} data-testid="register-role-select">
              <SelectTrigger data-testid="register-role-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(form.role === 'subcontractor' || form.role === 'inspector') && packages.length > 0 && (
            <div className="space-y-2">
              <Label>Assigned Packages</Label>
              <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto p-2 border rounded-lg bg-slate-50">
                {packages.map(pkg => (
                  <label key={pkg} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white p-1.5 rounded">
                    <Checkbox checked={form.packages.includes(pkg)} onCheckedChange={() => togglePackage(pkg)} data-testid={`pkg-checkbox-${pkg.replace(/[\s/]+/g, '-').toLowerCase()}`} />
                    <span className="text-slate-700 text-xs">{pkg}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="register-submit-button">
            {loading ? 'Creating...' : 'Create Account'}
          </Button>
          <p className="text-center text-sm text-slate-500">
            Already registered? <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">Sign In</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
